import { useCallback, useMemo } from 'react'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { useCoMingleShipperScope } from '@/components/system/useCoMingleShipperScope'
import { type ShipperScope, withCoMingle, shipperScopeOf } from '@/components/system/shipperScope'
import { lockShipperName } from '@/components/system/shipperScopeOptions'
import { InterfaceHistoryParams } from '@/services/history/history.types'
import { FormProvider, useForm } from 'react-hook-form'
import { useGetInterfaceHistory } from '@/services/history/useHistoryQuries'
import { MAX_PERIOD_DAYS_ON_INTERFACE_HISTORY } from '@/services/history/consts'
import { InterfaceHistoryForm, scopedDefaultValues } from '@/pages/history/interfaceHistory/components/InterfaceHistoryForm'
import { InterfaceHistoryTable } from '@/pages/history/interfaceHistory/components/InterfaceHistoryTable'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { flushSync } from 'react-dom'
import { differenceInDays, parse } from 'date-fns'
import MAlert from '@/utils/modal/MAlert'
import { PageLayout } from '@/components/layout/PageLayout'
import TitleSection from '@/components/content/TitleSection'
import { VStack } from '@musinsa/mwms-ds'

const InterfaceHistory = () => {
  const { ready, coMingle } = useCoMingleShipperScope()
  const scope = useMemo(() => withCoMingle(shipperScopeOf(AuthStorage.user.get()), coMingle), [coMingle])
  return ready ? <InterfaceHistoryContent scope={scope} /> : null
}

const InterfaceHistoryContent = ({ scope }: { scope: ShipperScope }) => {
  const { reload } = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<InterfaceHistoryParams>(scopedDefaultValues(scope))
  const form = useForm<InterfaceHistoryParams>({
    defaultValues: lockShipperName(parsedQs, scope),
  })
  // 조회 훅이 폼 값을 그대로 쓰므로 여기서 덮는다 — 세션·URL 저장도 같은 값이다.
  const values = lockShipperName(form.watch(), scope)
  const { data, isError, isFetching, refetch } = useGetInterfaceHistory(values)

  const onSearch = useCallback(() => {
    const [startDate, endDate] = values.plnDate
    if (!startDate || !endDate) {
      MAlert.show('실행일자를 입력해 주세요.')
      return
    }
    const daysDifference = differenceInDays(parse(endDate as string, 'yyyyMMdd', new Date()), parse(startDate as string, 'yyyyMMdd', new Date()))

    if (daysDifference >= MAX_PERIOD_DAYS_ON_INTERFACE_HISTORY) {
      MAlert.show(`실행일자는 ${MAX_PERIOD_DAYS_ON_INTERFACE_HISTORY}일 이내만 가능합니다.`)
      return
    }

    MenuStorage.menuOnSession.setSearch(values)
    flushSync(() => replaceQuery(values))
    reload()
    refetch()
  }, [values])

  return (
    <FormProvider {...form}>
      <PageLayout gap={16}>
        <VStack gap={12}>
          <TitleSection variant="legacy" title="I/F History" />
          <InterfaceHistoryForm onSearch={onSearch} scope={scope} />
        </VStack>

        {/* 재조회가 실패해도 react-query 는 직전 결과를 남긴다 — 새 조건의 결과로 오해하지 않게 비운다 */}
        <InterfaceHistoryTable data={isError ? [] : data?.list ?? []} isLoading={isFetching} />
      </PageLayout>
    </FormProvider>
  )
}

export default InterfaceHistory
